import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { isDemoUser } from "@/lib/demo";
import { DemoGuardModal } from "@/components/demo/DemoGuardModal";

interface DemoContextType {
  isDemoAccount: boolean;
  showDemoModal: () => void;
  guardAction: <T>(action: () => T) => T | undefined;
}

const DemoContext = createContext<DemoContextType | undefined>(undefined);

export function DemoProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const isDemoAccount = isDemoUser(profile?.phone);

  const showDemoModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const guardAction = useCallback(
    <T,>(action: () => T): T | undefined => {
      if (isDemoAccount) {
        setModalOpen(true);
        return undefined;
      }
      return action();
    },
    [isDemoAccount]
  );

  return (
    <DemoContext.Provider value={{ isDemoAccount, showDemoModal, guardAction }}>
      {children}
      <DemoGuardModal open={modalOpen} onOpenChange={setModalOpen} />
    </DemoContext.Provider>
  );
}

export function useDemo() {
  const context = useContext(DemoContext);
  if (context === undefined) {
    throw new Error("useDemo must be used within a DemoProvider");
  }
  return context;
}
